import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../api/axios';
import Header from '../../components/Dashboard/Header/Header';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import {
  Edit3,
  Package,
  Layers,
  IndianRupee,
  FileText,
  Activity,
  Calendar,
  Zap,
  Tag,
  ArrowLeft,
  Loader2,
  Building,
  User,
  Clock,
  ExternalLink
} from 'lucide-react';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import styles from './WorkOrderView.module.css';
import Footer from '../../components/Common/Footer/Footer';
import WorkOrderDrawer from '../../components/Projects/WorkOrderDrawer';
import NotFound from '../Error/NotFound';


const WorkOrderView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);

  const fetchItem = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/work-order-items/${id}`);
      if (response.data.success) {
        setItem(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching work order item:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [id]);

  if (loading) {
    return (
      <div className={styles['work-order-view-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
            <p style={{ color: '#64748b', fontWeight: 600 }}>Loading item details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return <NotFound />;
  }


  return (
    <div className={styles['work-order-view-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Standard Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Work Order Detail</h1>
              <p>Full breakdown and linked tracking for this line item</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate(-1)}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Edit Item</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['wo-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>{item.item_name}</h1>
                <span className={`${styles['status-badge']} ${styles['status-active']}`}>
                  Active
                </span>
              </div>
            </div>

            <div className={styles['wo-meta']}>
              <div className={styles['meta-item']}>
                <Building size={16} />
                <span>Project: <strong>{item.project?.project_name}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Tag size={16} />
                <span>Reference: <strong>#WOI-{item.id}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <User size={16} />
                <span>Created By: <strong>{item.creator?.name || 'Admin'}</strong></span>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={`${styles['m-icon']} ${styles['qty']}`}>
                <Layers size={22} />
              </div>
              <div className={styles['m-info']}>
                <span>Total Quantity</span>
                <h3>{parseFloat(item.quantity).toLocaleString()} <small style={{ fontSize: '13px', color: '#64748b' }}>{item.unit?.unit_name}</small></h3>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={`${styles['m-icon']} ${styles['rate']}`}>
                <IndianRupee size={22} />
              </div>
              <div className={styles['m-info']}>
                <span>Sales Price</span>
                <h3>₹{parseFloat(item.sales_price).toLocaleString()}</h3>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={`${styles['m-icon']} ${styles['total']}`}>
                <Zap size={22} />
              </div>
              <div className={styles['m-info']}>
                <span>Gross Value</span>
                <h3>₹{parseFloat(item.total_amount).toLocaleString()}</h3>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className={styles['dashboard-grid']}>
            {/* Left Column */}
            <div className={styles['left-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}>
                    <Package size={20} />
                  </div>
                  <h3>Item Specifications</h3>
                </div>
                <div className={styles['info-table']}>
                  <div className={styles['s-row']}>
                    <span>Item Name</span>
                    <span>{item.item_name}</span>
                  </div>
                  <div className={styles['s-row']}>
                    <span>HSN Code</span>
                    <span>{item.hsn_code || 'N/A'}</span>
                  </div>
                  <div className={styles['s-row']}>
                    <span>Unit of Measurement</span>
                    <span>{item.unit?.unit_name}</span>
                  </div>
                  <div className={styles['s-row']}>
                    <span>Tax Slab</span>
                    <span>{item.tax?.tax_name} ({item.tax?.tax_percentage}%)</span>
                  </div>
                  <div className={styles['s-row']}>
                    <span>Tax Amount</span>
                    <span>₹{parseFloat(item.tax_amount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}>
                    <FileText size={20} />
                  </div>
                  <h3>Service Description</h3>
                </div>
                <div className={styles['description-text']}>
                  {item.description || 'No detailed description available for this work order item.'}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className={styles['right-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}>
                    <Activity size={20} />
                  </div>
                  <h3>Financial Summary</h3>
                </div>
                <div className={styles['info-table']}>
                  <div className={styles['s-row']}>
                    <span>Subtotal</span>
                    <span>₹{(item.quantity * item.sales_price).toLocaleString()}</span>
                  </div>
                  <div className={styles['s-row']}>
                    <span>Applicable Tax</span>
                    <span>₹{parseFloat(item.tax_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className={styles['s-row']} style={{ padding: '16px 0', borderBottom: 'none' }}>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>Net Amount</span>
                    <span style={{ fontSize: '18px', fontWeight: 900, color: '#f97316' }}>₹{parseFloat(item.total_amount).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}>
                    <Clock size={20} />
                  </div>
                  <h3>History & Trail</h3>
                </div>
                <div className={styles['timeline-list']}>
                  <div className={styles['timeline-item']}>
                    <div className={styles['tl-icon']}>
                      <User size={16} />
                    </div>
                    <div className={styles['tl-content']}>
                      <h4>Created By</h4>
                      <p>{item.creator?.name || 'System Admin'}</p>
                    </div>
                  </div>
                  <div className={styles['timeline-item']}>
                    <div className={styles['tl-icon']}>
                      <Calendar size={16} />
                    </div>
                    <div className={styles['tl-content']}>
                      <h4>Creation Date</h4>
                      <p>{new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                  {item.project && (
                    <div className={styles['timeline-item']}>
                      <div className={styles['tl-icon']}>
                        <ExternalLink size={16} />
                      </div>
                      <div className={styles['tl-content']}>
                        <h4>Linked Project</h4>
                        <Link to={`/projects/${item.project_id}`} style={{ color: '#f97316', textDecoration: 'none', fontSize: '12px', fontWeight: 700 }}>
                          {item.project?.project_name.substring(0, 25)}...
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <WorkOrderDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={fetchItem}
        mode="edit"
        initialData={item}
      />
    </div>
  );
};

export default WorkOrderView;
